import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { debounce, get, isString } from 'lodash';
import React from 'react';
import iconSearch from 'src/asset/icons/icon-search.svg';
import FlagCountry from 'src/components/common/FlagCountry';

// Components
import PaperSelect from 'src/components/common/PaperSelect';

// Utilities
import { useQueryPeopleInformationSuggestion } from 'src/services/queries';
import { EmptyText } from 'src/utils/constants/config';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';

interface IProps {
  id: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  freeSolo?: boolean;
  countryCode?: string[];
}

const FormItemSuggestCompany = ({ disabled, id, required, freeSolo, error, countryCode = [] }: IProps) => {
  const { values, handleBlur, setFieldValue } = useFormikContext<TypeDTOSettingInformation>();
  const [query, setQuery] = React.useState('');
  const [selected, setSelected] = React.useState<{
    company_name: string;
    country_code: string;
  } | null>(null);

  const { data, isFetching } = useQueryPeopleInformationSuggestion(
    {
      company_name: query,
    },
    countryCode,
  );

  const onChange = (e: string) => {
    if (freeSolo) {
      setFieldValue(id, e);
    }
    setQuery(e);
  };

  const debounceOnchange = React.useCallback(debounce(onChange, 500), []);

  return (
    <Autocomplete
      id={id}
      defaultValue={{
        company_name: get(values, id, ''),
        country_code: '',
      }}
      loading={isFetching}
      onBlur={handleBlur}
      onChange={(_, value) => {
        if (isString(value)) {
          setFieldValue(id, value);
        } else {
          setFieldValue(id, value?.company_name);
          setSelected(value);
        }
      }}
      freeSolo={freeSolo}
      filterOptions={(option) => option}
      autoComplete
      disabled={disabled}
      fullWidth
      size="small"
      autoHighlight
      options={getArrayDefault(data.data)}
      PaperComponent={PaperSelect}
      getOptionLabel={(option) => {
        if (isString(option)) {
          return option;
        } else {
          return option?.company_name ?? EmptyText;
        }
      }}
      popupIcon={<Box component={'img'} src={iconSearch} />}
      renderOption={(props, option) => {
        return (
          <Box component="li" display="flex" flexDirection="row" {...props} key={option.id}>
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <FlagCountry width={20} code={option.country_code ?? ''} />
              <Typography variant="body2"> {option?.company_name ?? EmptyText} </Typography>
            </Stack>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: freeSolo ? <Box component={'img'} src={iconSearch} /> : <></>,
            startAdornment: selected ? <FlagCountry width={20} code={selected?.country_code ?? ''} /> : null,
          }}
          onChange={(e) => debounceOnchange(e.target.value)}
          required={required}
          label="Company Name"
        />
      )}
    />
  );
};

export default FormItemSuggestCompany;
